import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SIGNUP_CLOSED_REASON,
  normalizeSlug,
  signupInputSchema,
  type SignupInput,
  type SignupResponse,
} from '@castinflow/shared';
import { Alert, Button, Field, Input, RadioGroup, Spinner, buttonClass } from '@castinflow/ui';
import { ApiError, apiFetch } from '../lib/api';
import { SIGNUP_STATUS_QUERY_KEY, useSignupStatus } from '../lib/signup-status';
import { usePageMeta } from '../shell/page-meta';
import { PublicCard } from '../shell/PublicLayout';
import { tr } from '../i18n/tr';

interface SlugAvailability {
  slug: string;
  available: boolean;
  reason?: string;
}

/**
 * Kayıt sayfası (M7 T1, K-M7-02): kayıt kapısının durumu sunucudan okunur. Kapı kapalıysa form GÖSTERİLMEZ ve dürüst bir
 * "kayıt kapalı" durumu görünür (bağlantı gizlense de sayfaya doğrudan gidilebilir); durum okunamadıysa form yine
 * gösterilmez. Güvenlik sınırı API'dedir: kapalıyken `POST /signup` reddedilir.
 */
export function SignupPage() {
  const status = useSignupStatus();
  const closed = status.data?.open === false;
  usePageMeta(
    closed
      ? tr.auth.signupClosedTitle
      : status.isError
        ? tr.auth.signupStatusErrorTitle
        : tr.auth.signupTitle,
  );
  if (status.data?.open === true) return <SignupForm />;
  if (status.isPending)
    return (
      <PublicCard>
        <div
          className="flex justify-center py-6"
          aria-busy="true"
          data-testid="signup-status-loading"
        >
          <Spinner label={tr.auth.signupStatusLoading} />
        </div>
      </PublicCard>
    );
  return (
    <PublicCard>
      <section className="space-y-3" data-testid={closed ? 'signup-closed' : 'signup-status-error'}>
        <h1
          tabIndex={-1}
          className="text-xl font-semibold text-fg focus:outline-none"
          data-page-title=""
        >
          {closed ? tr.auth.signupClosedTitle : tr.auth.signupStatusErrorTitle}
        </h1>
        <p className="text-base text-fg-muted">
          {closed ? tr.auth.signupClosedBody : tr.auth.signupStatusErrorBody}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/login" className={buttonClass('primary')}>
            {tr.auth.submitLogin}
          </Link>
          {closed ? null : (
            <Button
              loading={status.isFetching}
              onClick={() => {
                void status.refetch();
              }}
              data-testid="signup-status-retry"
            >
              {tr.auth.signupStatusRetry}
            </Button>
          )}
        </div>
      </section>
    </PublicCard>
  );
}

/** Kayıt ol → çalışma alanı oluştur (WP 1.2). Yalnız kayıt kapısı açıkken çizilir. */
function SignupForm() {
  const [form, setForm] = useState<SignupInput>({
    companyName: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    workspaceKind: 'real',
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const slug = normalizeSlug(form.companyName);
  usePageMeta(tr.auth.signupTitle);

  const availability = useQuery({
    queryKey: ['slug-availability', slug],
    queryFn: () =>
      apiFetch<SlugAvailability>(`/signup/slug-availability?slug=${encodeURIComponent(slug)}`),
    enabled: slug.length >= 3,
  });

  const [idempotencyKey] = useState(() => `web-${crypto.randomUUID()}`);
  const queryClient = useQueryClient();
  const signup = useMutation({
    mutationFn: (input: SignupInput) =>
      apiFetch<SignupResponse>('/signup', {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'idempotency-key': idempotencyKey },
        body: JSON.stringify(input),
      }),
    // Form açıkken kapı sunucuda kapandıysa (403 `signup_closed`) durum yeniden okunur: sayfa "kayıt kapalı" durumuna geçer
    onError: (e) => {
      if (
        e instanceof ApiError &&
        e.status === 403 &&
        (e.details as { reason?: string } | undefined)?.reason === SIGNUP_CLOSED_REASON
      )
        void queryClient.invalidateQueries({ queryKey: SIGNUP_STATUS_QUERY_KEY });
    },
  });

  function update<K extends keyof SignupInput>(key: K, value: SignupInput[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = signupInputSchema.safeParse(form);
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const k = issue.path[0];
        if (typeof k === 'string' && !(k in errs)) errs[k] = issue.message;
      }
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    signup.mutate(parsed.data);
  }

  if (signup.isSuccess) {
    return (
      <PublicCard>
        <section data-testid="signup-success" className="space-y-3">
          <h1
            tabIndex={-1}
            className="text-xl font-semibold text-fg focus:outline-none"
            data-page-title=""
          >
            {tr.auth.signupDoneTitle}
          </h1>
          <p className="text-base text-fg-muted">{tr.auth.signupDoneBody}</p>
          <p className="rounded border border-border bg-surface-subtle px-3 py-2 text-base">
            <code className="font-mono" data-testid="tenant-slug">
              {signup.data.tenant.slug}
            </code>
          </p>
          <Link to="/login" className={buttonClass('primary')}>
            {tr.auth.submitLogin}
          </Link>
        </section>
      </PublicCard>
    );
  }

  const apiError = signup.error instanceof ApiError ? signup.error : null;

  return (
    <PublicCard>
      <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-form" noValidate>
        <div>
          <h1
            tabIndex={-1}
            className="text-xl font-semibold text-fg focus:outline-none"
            data-page-title=""
          >
            {tr.auth.signupTitle}
          </h1>
          <p className="mt-1 text-base text-fg-muted">{tr.auth.signupIntro}</p>
        </div>
        <Field
          label={tr.auth.companyName}
          error={fieldErrors['companyName']}
          description={
            slug.length >= 3 ? (
              <span data-testid="slug-hint">
                {tr.auth.addressPreview}: <code className="font-mono">{slug}</code>
                {availability.data
                  ? ` · ${availability.data.available ? tr.auth.addressAvailable : tr.auth.addressTaken}`
                  : ''}
              </span>
            ) : undefined
          }
        >
          <Input
            value={form.companyName}
            onChange={(e) => {
              update('companyName', e.target.value);
            }}
            autoComplete="organization"
            data-testid="companyName"
          />
        </Field>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={tr.auth.firstName} error={fieldErrors['firstName']}>
            <Input
              value={form.firstName}
              onChange={(e) => {
                update('firstName', e.target.value);
              }}
              autoComplete="given-name"
              data-testid="firstName"
            />
          </Field>
          <Field label={tr.auth.lastName} error={fieldErrors['lastName']}>
            <Input
              value={form.lastName}
              onChange={(e) => {
                update('lastName', e.target.value);
              }}
              autoComplete="family-name"
              data-testid="lastName"
            />
          </Field>
        </div>
        <Field label={tr.auth.email} error={fieldErrors['email']}>
          <Input
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={(e) => {
              update('email', e.target.value);
            }}
            data-testid="email"
          />
        </Field>
        <Field
          label={tr.auth.passwordLabel}
          description={tr.auth.passwordHint}
          error={fieldErrors['password']}
        >
          <Input
            type="password"
            autoComplete="new-password"
            value={form.password}
            onChange={(e) => {
              update('password', e.target.value);
            }}
            data-testid="password"
          />
        </Field>
        <RadioGroup
          legend={tr.auth.workspaceKind}
          name="workspace-kind"
          value={form.workspaceKind}
          onChange={(v) => {
            update('workspaceKind', v);
          }}
          options={[
            { value: 'real', label: tr.auth.workspaceReal, description: tr.auth.workspaceRealHint },
            { value: 'demo', label: tr.auth.workspaceDemo, description: tr.auth.workspaceDemoHint },
          ]}
        />
        {apiError ? (
          <div data-testid="signup-error">
            <Alert
              tone="danger"
              announce
              title={apiError.code === 'CONFLICT' ? tr.auth.signupConflict : tr.auth.signupError}
            >
              <span className="text-sm">
                {tr.auth.referenceCode}: <code className="font-mono">{apiError.requestId}</code>
              </span>
            </Alert>
          </div>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          className="w-full"
          loading={signup.isPending}
          data-testid="submit"
        >
          {tr.auth.submitSignup}
        </Button>
        <p className="text-base text-fg-muted">
          {tr.auth.haveAccount}{' '}
          <Link
            to="/login"
            className="font-medium text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
          >
            {tr.auth.goLogin}
          </Link>
        </p>
      </form>
    </PublicCard>
  );
}
